import { Suspense, useState } from 'react';
import { MDXProvider } from '@mdx-js/react';
import { createFileRoute, Outlet, useParams } from '@tanstack/react-router';

import { getGetWorkflowNodesV2QueryOptions } from '@/api/gen/workflows-v2-nodes';
import { DocsContentTable } from '@/components/features/documentation/DocsContentTable';
import { DocsSidebar } from '@/components/features/documentation/DocsSidebar';
import { mdxComponents } from '@/components/features/documentation/mdx-components';
import { DocPageHeadingsContext } from '@/features/documentation/hooks/doc-page-headings';
import { DOC_SCROLL_CONTAINER_ID } from '@/features/documentation/hooks/use-doc-headings';
import { useDocsTree } from '@/features/documentation/hooks/use-docs-tree';
import type { DocHeading } from '@/features/documentation/utils/docs-tree-utils';
import { resolveDocPath } from '@/features/documentation/utils/docs-tree-utils';
import { devToolsQueryOptions } from '@/hooks/use-dev-tool-toggle';

export const Route = createFileRoute('/_protected/t/$tenantId/docs')({
  beforeLoad: async ({ context: { queryClient } }) => {
    const devTools = await queryClient.ensureQueryData(devToolsQueryOptions());
    // Warms the node registry so the tree is complete before the child route
    // resolves a path against it. Failure is swallowed on purpose, since a
    // reader without workflow access just gets a tree with no node pages.
    await queryClient.ensureQueryData(getGetWorkflowNodesV2QueryOptions()).catch(() => undefined);
    return { devTools };
  },
  component: DocumentationLayout,
});

function DocumentationLayout() {
  const { user } = Route.useRouteContext();
  // Read from the child match rather than the pathname, so the tenant prefix
  // never has to be sliced back off. The index route has no splat — the home page.
  const splat = useParams({ strict: false, select: (params) => params._splat }) ?? '';
  const tree = useDocsTree();
  const [pageHeadings, setPageHeadings] = useState<DocHeading[]>();
  const headings = pageHeadings ?? resolveDocPath(splat, tree)?.at(-1)?.headings ?? [];

  return (
    <div className="flex size-full min-h-0">
      <DocsSidebar user={user} tree={tree} />
      <MDXProvider components={mdxComponents}>
        <div id={DOC_SCROLL_CONTAINER_ID} className="min-h-0 flex-1 overflow-auto">
          <Suspense
            fallback={
              <div className="mx-auto w-full max-w-3xl space-y-4 px-8 py-8">
                <div className="h-8 w-1/2 animate-pulse rounded bg-white/10" />
                <div className="h-4 w-full animate-pulse rounded bg-white/10" />
                <div className="h-4 w-5/6 animate-pulse rounded bg-white/10" />
              </div>
            }
          >
            <DocPageHeadingsContext value={setPageHeadings}>
              <Outlet />
            </DocPageHeadingsContext>
          </Suspense>
        </div>
      </MDXProvider>

      <DocsContentTable headings={headings} />
    </div>
  );
}
